// lightbox-geometry.ts — the maths behind the image lightbox (SAY-1806):
// where the enlarged image sits in the viewport and the FLIP transform that
// makes it grow out of the thumbnail the user clicked. Pure so it is unit-tested;
// ImageLightbox.svelte owns the DOM + Web Animations wiring.

export type Rect = { x: number; y: number; w: number; h: number };
export type Size = { w: number; h: number };

export const LIGHTBOX_MARGIN = 24;

/** Where a `natural`-sized image lands when fitted (contain) into the viewport
 *  with `margin` on every side and centered. Scales up as well as down: the
 *  point of the lightbox is a LARGE preview, so a small image fills the stage. */
export function fitRect(natural: Size, viewport: Size, margin: number = LIGHTBOX_MARGIN): Rect {
	const availW = Math.max(1, viewport.w - 2 * margin);
	const availH = Math.max(1, viewport.h - 2 * margin);
	const nw = Math.max(1, natural.w);
	const nh = Math.max(1, natural.h);
	const scale = Math.min(availW / nw, availH / nh);
	const w = nw * scale;
	const h = nh * scale;
	return { x: (viewport.w - w) / 2, y: (viewport.h - h) / 2, w, h };
}

/** The rect the full image occupies "behind" an object-cover thumbnail: the
 *  smallest rect of the image's aspect ratio that covers `thumb`, centered on it.
 *  Animating from THIS rect (instead of the clipped thumbnail box) keeps the
 *  scale uniform, so the image un-clips instead of stretching. */
export function coverRect(thumb: Rect, natural: Size): Rect {
	const nw = Math.max(1, natural.w);
	const nh = Math.max(1, natural.h);
	const scale = Math.max(thumb.w / nw, thumb.h / nh);
	const w = nw * scale;
	const h = nh * scale;
	return { x: thumb.x + (thumb.w - w) / 2, y: thumb.y + (thumb.h - h) / 2, w, h };
}

/** CSS transform that moves an element laid out at `to` so it visually sits at
 *  `from` (the FLIP "invert" step). Origin must be the element's top-left. */
export function flipTransform(from: Rect, to: Rect): string {
	const sx = to.w > 0 ? from.w / to.w : 1;
	const sy = to.h > 0 ? from.h / to.h : 1;
	const dx = from.x - to.x;
	const dy = from.y - to.y;
	return `translate(${round(dx)}px, ${round(dy)}px) scale(${round(sx)}, ${round(sy)})`;
}

function round(n: number): number {
	return Math.round(n * 1000) / 1000;
}
